import { Box, Paper, Typography } from '@mui/material';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';

// Our Components
import ConfirmKeyActionChange from 'components/Modal/KeyActions/ConfirmKeyActionChange';
import { PrimaryButton } from 'components/Button/Button';
import ConfigureMortgageRefi from 'components/Forms/KeyActions/configure/ConfigureMortgageRefi';
import Loader from 'components/Loader';

// Our Hooks
import useGetKeyActionQualifiers from 'hooks/keyActions/useGetKeyActionQualifiers';
import useMutateChangeKeyActionQualifier from 'hooks/keyActions/useMutateChangeKeyActionQualifier';

// Our Atoms
import keyActionQualifierAtom from 'atoms/keyActionQualifierAtom';

// Our Utils
import formatDate from 'shared/utils/date/formatDate';

// Our Constants
import {
	SUPPORTED_KEY_ACTIONS,
	MORTGAGE_REFI_1
} from 'shared/constants/KeyActions/KeyActionTypes';

// Local Constants
const CONFIGURE_KEY_ACTION_FORM_MAP = {
	[MORTGAGE_REFI_1]: ConfigureMortgageRefi
};

function ConfigureKeyActions() {
	const [isConfigureKeyActionModalOpen, setIsConfigureKeyActionModalOpen] =
		useState(false);
	const [selectedKeyActionQualifier, setSelectedKeyActionQualifier] =
		useState(null);
	const [isFormReady, setIsFormReady] = useState(false);

	const [currentKeyActionQualifier] = useAtom(keyActionQualifierAtom);

	const {
		data: keyActionQualifiers,
		isLoading: isKeyActionQualifiersLoading,
		isSuccess: isKeyActionQualifiersSuccess
	} = useGetKeyActionQualifiers();

	const changeKeyActionQualifier = useMutateChangeKeyActionQualifier();

	const toggleConfigureKeyActionModal = useCallback(() => {
		setIsConfigureKeyActionModalOpen((prev) => !prev);
	}, []);

	const selectedKeyActionType = selectedKeyActionQualifier?.type;

	const ConfigureKeyActionForm = selectedKeyActionType
		? CONFIGURE_KEY_ACTION_FORM_MAP[selectedKeyActionType]
		: null;

	if (isKeyActionQualifiersLoading) {
		return <Loader />;
	}
	if (isKeyActionQualifiersSuccess) {
		const selectedKeyActionTitle = selectedKeyActionType
			? SUPPORTED_KEY_ACTIONS[selectedKeyActionType].title
			: '';

		return (
			<>
				{selectedKeyActionQualifier && (
					<ConfirmKeyActionChange
						isOpen={isConfigureKeyActionModalOpen}
						handleClose={toggleConfigureKeyActionModal}
						title={`Configuring the following key action: ${selectedKeyActionTitle}`}
						subtitle={`Warning: This will update the key action for all users`}
						primaryButtonText="Save changes"
						confirmButtonCallback={() => {
							const formattedKeyActionDate = formatDate(
								currentKeyActionQualifier.mortgageOriginationDateQualifier
							);

							changeKeyActionQualifier.mutate(
								{
									...selectedKeyActionQualifier,
									keyActionQualification: {
										...selectedKeyActionQualifier.keyActionQualification,
										mortgageOriginationDateQualifier:
											formattedKeyActionDate
									}
								},
								{
									onSuccess: () => {
										toggleConfigureKeyActionModal();
									}
								}
							);
						}}
						isConfirmButtonLoading={
							changeKeyActionQualifier.isLoading
						}
						disable={!isFormReady}
					>
						<ConfigureKeyActionForm
							setIsFormReady={setIsFormReady}
							selectedKeyActionQualifier={
								selectedKeyActionQualifier
							}
						/>
					</ConfirmKeyActionChange>
				)}

				{keyActionQualifiers.map((currentKeyAction) => {
					const { keyAction, title, description } =
						SUPPORTED_KEY_ACTIONS[currentKeyAction.type];

					return (
						<Box
							component={Paper}
							elevation={5}
							key={keyAction}
							sx={{
								borderRadius: 5,
								display: 'flex',
								flexDirection: 'column',
								gap: 5,
								p: 2,
								maxWidth: 450
							}}
						>
							<Typography variant="h6">
								Key Action: {title}
							</Typography>
							<Typography variant="body1">
								{description}
							</Typography>
							<PrimaryButton
								onClick={() => {
									setSelectedKeyActionQualifier(
										currentKeyAction
									);
									toggleConfigureKeyActionModal();
								}}
							>
								Configure
							</PrimaryButton>
						</Box>
					);
				})}
			</>
		);
	}
}

export default ConfigureKeyActions;
